import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { PageLayout } from "../components/PageLayout";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Palette,
  Type,
  Image,
  Upload,
  Save,
  RotateCcw,
  Eye,
  Mail,
  Plus,
  X,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const AdminPersonalizationPage = () => {
  const { settings, updateSettings, refreshSettings } = useSiteSettings();
  const [formData, setFormData] = useState({
    site_name: "",
    site_subtitle: "",
    logo_url: "",
    primary_color: "#B87A8A",
    secondary_color: "#F3F0FA",
    accent_color: "#C5A065",
    background_color: "#FFFCF9",
    text_color: "#2D2A32",
    hero_image_url: "",
    footer_text: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [savingEmails, setSavingEmails] = useState(false);
  const logoInputRef = useRef(null);
  const heroInputRef = useRef(null);

  useEffect(() => {
    if (settings) {
      setFormData({
        site_name: settings.site_name || "",
        site_subtitle: settings.site_subtitle || "",
        logo_url: settings.logo_url || "",
        primary_color: settings.primary_color || "#B87A8A",
        secondary_color: settings.secondary_color || "#F3F0FA",
        accent_color: settings.accent_color || "#C5A065",
        background_color: settings.background_color || "#FFFCF9",
        text_color: settings.text_color || "",
        hero_image_url: settings.hero_image_url || "",
        footer_text: settings.footer_text || "",
      });
    }
    fetchNotificationEmails();
  }, [settings]);

  const fetchNotificationEmails = async () => {
    try {
      const response = await axios.get(`${API}/admin/notification-emails`);
      setNotificationEmails(response.data.notification_emails || []);
    } catch (error) {
      console.error("Error fetching notification emails:", error);
    }
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Inserisci un'email valida");
      return;
    }
    if (notificationEmails.includes(newEmail)) {
      toast.error("Email già presente");
      return;
    }
    setNotificationEmails([...notificationEmails, newEmail]);
    setNewEmail("");
  };

  const handleRemoveEmail = (email) => {
    setNotificationEmails(notificationEmails.filter((e) => e !== email));
  };

  const handleSaveEmails = async () => {
    setSavingEmails(true);
    try {
      await axios.put(`${API}/admin/notification-emails`, {
        notification_emails: notificationEmails,
      });
      toast.success("Email di notifica salvate!");
    } catch (error) {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSavingEmails(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileUpload = async (file, fieldName) => {
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      // Construct full URL
      const fullUrl = `${window.location.origin}${response.data.url}`;
      setFormData({ ...formData, [fieldName]: fullUrl });
      toast.success("Immagine caricata con successo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateSettings(formData);
      toast.success("Impostazioni salvate con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    const defaults = {
      site_name: "ODR Academy",
      site_subtitle: "Estetica Oncologica",
      logo_url: "",
      primary_color: "#B87A8A",
      secondary_color: "#F3F0FA",
      accent_color: "#C5A065",
      background_color: "#FFFCF9",
      text_color: "#2D2A32",
      hero_image_url: "",
      footer_text: "© 2025 ODR Academy - Tutti i diritti riservati",
    };

    setSaving(true);
    try {
      await updateSettings(defaults);
      setFormData(defaults);
      toast.success("Impostazioni ripristinate ai valori predefiniti");
    } catch (error) {
      toast.error("Errore durante il ripristino");
    } finally {
      setSaving(false);
    }
  };

  const colorPresets = [
    { name: "Rosa Antico", primary: "#B87A8A", secondary: "#F3F0FA", accent: "#C5A065" },
    { name: "Verde Salvia", primary: "#7A9E7E", secondary: "#F0F5F1", accent: "#D4A574" },
    { name: "Blu Polvere", primary: "#7A8EB8", secondary: "#F0F3FA", accent: "#C5A065" },
    { name: "Lavanda", primary: "#9B7AAE", secondary: "#F5F0FA", accent: "#C5A065" },
    { name: "Corallo", primary: "#CD8B76", secondary: "#FFF5F0", accent: "#8B9E7A" },
  ];

  const applyColorPreset = (preset) => {
    setFormData({
      ...formData,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent,
    });
  };

  return (
    <PageLayout>
      

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Personalizzazione
            </h1>
            <p className="text-muted-foreground">
              Personalizza colori, scritte e immagini del sito
            </p>
          </div>
          <div className="flex gap-2 mt-4 md:mt-0">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={resetToDefaults}
              disabled={saving}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Ripristina
            </Button>
            <Button
              className="rounded-full"
              onClick={handleSubmit}
              disabled={saving}
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Salvataggio..." : "Salva Modifiche"}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="identity" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 rounded-full p-1 bg-secondary">
            <TabsTrigger value="identity" className="rounded-full">
              <Type className="w-4 h-4 mr-2" />
              Identità
            </TabsTrigger>
            <TabsTrigger value="colors" className="rounded-full">
              <Palette className="w-4 h-4 mr-2" />
              Colori
            </TabsTrigger>
            <TabsTrigger value="images" className="rounded-full">
              <Image className="w-4 h-4 mr-2" />
              Immagini
            </TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-full">
              <Mail className="w-4 h-4 mr-2" />
              Notifiche
            </TabsTrigger>
          </TabsList>

          {/* Identity Tab */}
          <TabsContent value="identity">
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Identità del Sito</CardTitle>
                <CardDescription>Nome, sottotitolo e testi principali</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="site_name">Nome del Sito</Label>
                    <Input
                      id="site_name"
                      name="site_name"
                      value={formData.site_name}
                      onChange={handleChange}
                      placeholder="ODR Academy"
                      className="rounded-xl"
                      data-testid="site-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="site_subtitle">Sottotitolo</Label>
                    <Input
                      id="site_subtitle"
                      name="site_subtitle"
                      value={formData.site_subtitle}
                      onChange={handleChange}
                      placeholder="Estetica Oncologica"
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="footer_text">Testo Footer</Label>
                  <Input
                    id="footer_text"
                    name="footer_text"
                    value={formData.footer_text}
                    onChange={handleChange}
                    placeholder="© 2025 ODR Academy - Tutti i diritti riservati"
                    className="rounded-xl"
                  />
                </div>

                {/* Preview */}
                <div className="p-6 bg-secondary/50 rounded-xl">
                  <p className="text-sm text-muted-foreground mb-3">Anteprima:</p>
                  <div className="flex items-center gap-3">
                    {formData.logo_url ? (
                      <img src={formData.logo_url} alt="Logo" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-white font-serif font-bold text-xl">
                          {formData.site_name?.charAt(0) || "O"}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="font-serif text-xl font-semibold">{formData.site_name || "ODR Academy"}</h3>
                      <p className="text-sm text-muted-foreground">{formData.site_subtitle || "Estetica Oncologica"}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colors Tab */}
          <TabsContent value="colors">
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Palette Colori</CardTitle>
                <CardDescription>Personalizza i colori del tema</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Color Presets */}
                <div>
                  <Label className="mb-3 block">Preset Colori</Label>
                  <div className="flex flex-wrap gap-2">
                    {colorPresets.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => applyColorPreset(preset)}
                        className="flex items-center gap-2 px-3 py-2 rounded-full border border-border hover:border-primary transition-colors"
                      >
                        <div className="flex -space-x-1">
                          <div className="w-4 h-4 rounded-full border border-white" style={{ backgroundColor: preset.primary }} />
                          <div className="w-4 h-4 rounded-full border border-white" style={{ backgroundColor: preset.secondary }} />
                          <div className="w-4 h-4 rounded-full border border-white" style={{ backgroundColor: preset.accent }} />
                        </div>
                        <span className="text-sm">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Pickers */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="primary_color">Colore Primario</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="primary_color"
                        name="primary_color"
                        value={formData.primary_color}
                        onChange={handleChange}
                        className="w-12 h-12 rounded-xl cursor-pointer border-0"
                      />
                      <Input
                        value={formData.primary_color}
                        onChange={handleChange}
                        name="primary_color"
                        className="rounded-xl font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secondary_color">Colore Secondario</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="secondary_color"
                        name="secondary_color"
                        value={formData.secondary_color}
                        onChange={handleChange}
                        className="w-12 h-12 rounded-xl cursor-pointer border-0"
                      />
                      <Input
                        value={formData.secondary_color}
                        onChange={handleChange}
                        name="secondary_color"
                        className="rounded-xl font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accent_color">Colore Accento</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="accent_color"
                        name="accent_color"
                        value={formData.accent_color}
                        onChange={handleChange}
                        className="w-12 h-12 rounded-xl cursor-pointer border-0"
                      />
                      <Input
                        value={formData.accent_color}
                        onChange={handleChange}
                        name="accent_color"
                        className="rounded-xl font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="background_color">Sfondo</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="background_color"
                        name="background_color"
                        value={formData.background_color}
                        onChange={handleChange}
                        className="w-12 h-12 rounded-xl cursor-pointer border-0"
                      />
                      <Input
                        value={formData.background_color}
                        onChange={handleChange}
                        name="background_color"
                        className="rounded-xl font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="text_color">Testo</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="text_color"
                        name="text_color"
                        value={formData.text_color}
                        onChange={handleChange}
                        className="w-12 h-12 rounded-xl cursor-pointer border-0"
                      />
                      <Input
                        value={formData.text_color}
                        onChange={handleChange}
                        name="text_color"
                        className="rounded-xl font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Color Preview */}
                <div className="p-6 rounded-xl border border-border" style={{ backgroundColor: formData.background_color }}>
                  <p className="text-sm mb-4" style={{ color: formData.text_color }}>Anteprima colori:</p>
                  <div className="flex flex-wrap gap-3">
                    <button className="px-4 py-2 rounded-full text-white font-medium" style={{ backgroundColor: formData.primary_color }}>
                      Pulsante Primario
                    </button>
                    <button className="px-4 py-2 rounded-full font-medium" style={{ backgroundColor: formData.secondary_color, color: formData.primary_color }}>
                      Pulsante Secondario
                    </button>
                    <span className="px-3 py-1 rounded-full text-white text-sm" style={{ backgroundColor: formData.accent_color }}>
                      Badge Accento
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images">
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Immagini</CardTitle>
                <CardDescription>Logo e immagini del sito</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Logo Upload */}
                <div className="space-y-3">
                  <Label>Logo del Sito</Label>
                  <div className="flex items-start gap-4">
                    <div className="w-24 h-24 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border-2 border-dashed border-border">
                      {formData.logo_url ? (
                        <img src={formData.logo_url} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <Upload className="w-8 h-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], "logo_url")}
                      />
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? "Caricamento..." : "Carica Logo"}
                      </Button>
                      <p className="text-xs text-muted-foreground">Oppure inserisci URL:</p>
                      <Input
                        name="logo_url"
                        value={formData.logo_url}
                        onChange={handleChange}
                        placeholder="https://..."
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                </div>

                {/* Hero Image Upload */}
                <div className="space-y-3">
                  <Label>Immagine Hero (sfondo principale)</Label>
                  <div className="space-y-2">
                    <div className="w-full h-40 rounded-xl bg-secondary flex items-center justify-center overflow-hidden border-2 border-dashed border-border">
                      {formData.hero_image_url ? (
                        <img src={formData.hero_image_url} alt="Hero" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <Image className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">Nessuna immagine hero</p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        ref={heroInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], "hero_image_url")}
                      />
                      <Button
                        variant="outline"
                        className="rounded-full"
                        onClick={() => heroInputRef.current?.click()}
                        disabled={uploading}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploading ? "Caricamento..." : "Carica Immagine"}
                      </Button>
                      {formData.hero_image_url && (
                        <Button
                          variant="ghost"
                          className="rounded-full text-destructive"
                          onClick={() => setFormData({ ...formData, hero_image_url: "" })}
                        >
                          Rimuovi
                        </Button>
                      )}
                    </div>
                    <Input
                      name="hero_image_url"
                      value={formData.hero_image_url}
                      onChange={handleChange}
                      placeholder="https://... oppure carica un file"
                      className="rounded-xl"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="rounded-2xl border-border/50">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Email di Notifica</CardTitle>
                <CardDescription>
                  Inserisci gli indirizzi email che riceveranno una notifica quando qualcuno si iscrive a una sessione
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current emails */}
                <div className="space-y-3">
                  <Label>Email attive</Label>
                  {notificationEmails.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {notificationEmails.map((email) => (
                        <Badge 
                          key={email} 
                          variant="secondary" 
                          className="rounded-full py-2 px-4 text-sm flex items-center gap-2"
                        >
                          <Mail className="w-3 h-3" />
                          {email}
                          <button
                            onClick={() => handleRemoveEmail(email)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nessuna email configurata</p>
                  )}
                </div>

                {/* Add new email */}
                <div className="space-y-3">
                  <Label htmlFor="newEmail">Aggiungi nuova email</Label>
                  <div className="flex gap-2">
                    <Input
                      id="newEmail"
                      type="email"
                      placeholder="es. docente@email.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && handleAddEmail()}
                      className="rounded-xl flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      onClick={handleAddEmail}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi
                    </Button>
                  </div>
                </div>

                {/* Save button */}
                <Button
                  onClick={handleSaveEmails}
                  disabled={savingEmails}
                  className="rounded-full"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingEmails ? "Salvataggio..." : "Salva Email di Notifica"}
                </Button>

                {/* Info box */}
                <div className="p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-800">
                    <strong>ℹ️ Info:</strong> Quando una cliente si iscrive a una sessione, riceveranno una notifica:
                  </p>
                  <ul className="text-sm text-blue-700 mt-2 ml-4 list-disc">
                    <li>Tutte le email inserite qui sopra</li>
                    <li>L'email del docente associato alla sessione (se configurata)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default AdminPersonalizationPage;
